import React, { useMemo } from 'react';
import { FaCalendarAlt, FaClock, FaUser } from 'react-icons/fa';
import { useProContext } from '../context/ProContext';
import ProLayout from '../layout/ProLayout';

const RdvPage = () => {
  const { rdvList, brand } = useProContext();

  const todayStr = new Date().toDateString();
  const tomorrowStr = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toDateString(); })();

  // Group by day
  const grouped = useMemo(() => {
    const map = {};
    [...rdvList]
      .filter(r => r.date)
      .sort((a, b) => {
        const dateCmp = new Date(a.date).getTime() - new Date(b.date).getTime();
        if (dateCmp !== 0) return dateCmp;
        return (a.start_time || '').localeCompare(b.start_time || '');
      })
      .forEach(r => {
        const key = new Date(r.date).toDateString();
        if (!map[key]) map[key] = [];
        map[key].push(r);
      });
    return map;
  }, [rdvList]);

  const rdvToday = grouped[todayStr] || [];
  const rdvTomorrow = grouped[tomorrowStr] || [];
  const upcomingDays = Object.keys(grouped)
    .filter(k => {
      const d = new Date(k);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); tomorrow.setHours(0, 0, 0, 0);
      return d > tomorrow;
    })
    .sort((a, b) => new Date(a) - new Date(b));

  const formatDay = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  const RdvItem = ({ r }) => {
    const time = (r.start_time || '').slice(0, 5) || '—';
    return (
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
        <div className="w-14 text-center flex-shrink-0">
          <p className="text-sm font-bold text-gray-900 tabular-nums">{time}</p>
        </div>
        <div className="w-px h-10 bg-gray-200" />
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[0.6875rem] font-bold flex-shrink-0" style={{ backgroundColor: brand }}>
          {(r.user?.first_name || '?')[0]}{(r.user?.last_name || '?')[0]}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">{r.user?.first_name} {r.user?.last_name}</p>
          {r.reason && <p className="text-[0.6875rem] text-gray-500 truncate">{r.reason}</p>}
        </div>
      </div>
    );
  };

  return (
    <ProLayout title="Rendez-vous">
      <div className="p-6 max-w-4xl mx-auto space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-[0.6875rem] text-gray-400">Total</p>
            <p className="text-xl font-bold text-gray-900">{rdvList.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-[0.6875rem] text-gray-400">Aujourd'hui</p>
            <p className="text-xl font-bold" style={{ color: brand }}>{rdvToday.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-[0.6875rem] text-gray-400">Demain</p>
            <p className="text-xl font-bold text-gray-900">{rdvTomorrow.length}</p>
          </div>
        </div>

        {/* Today */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2" style={{ backgroundColor: brand + '08' }}>
            <FaClock className="w-3 h-3" style={{ color: brand }} />
            <h3 className="text-sm font-bold text-gray-900">Aujourd'hui</h3>
            <span className="text-[0.625rem] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: brand + '15', color: brand }}>{rdvToday.length}</span>
          </div>
          {rdvToday.length === 0 ? (
            <p className="text-sm text-gray-300 text-center py-8">Aucun rendez-vous aujourd'hui</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {rdvToday.map(r => <RdvItem key={r.id} r={r} />)}
            </div>
          )}
        </div>

        {/* Tomorrow */}
        {rdvTomorrow.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50">
              <FaCalendarAlt className="w-3 h-3 text-gray-500" />
              <h3 className="text-sm font-bold text-gray-900">Demain</h3>
              <span className="text-[0.625rem] font-bold text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">{rdvTomorrow.length}</span>
            </div>
            <div className="divide-y divide-gray-50">
              {rdvTomorrow.map(r => <RdvItem key={r.id} r={r} />)}
            </div>
          </div>
        )}

        {/* Upcoming days */}
        {upcomingDays.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-[0.6875rem] font-bold text-gray-400 uppercase tracking-wider px-1">À venir</h3>
            {upcomingDays.map(dayKey => (
              <div key={dayKey} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50">
                  <h4 className="text-[0.75rem] font-semibold text-gray-700 capitalize">{formatDay(dayKey)}</h4>
                </div>
                <div className="divide-y divide-gray-50">
                  {grouped[dayKey].map(r => <RdvItem key={r.id} r={r} />)}
                </div>
              </div>
            ))}
          </div>
        )}

        {rdvList.length === 0 && (
          <div className="text-center py-16">
            <FaCalendarAlt className="w-8 h-8 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">Aucun rendez-vous</p>
          </div>
        )}
      </div>
    </ProLayout>
  );
};

export default RdvPage;
